import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollLink = (sectionId) => {
    if (location.pathname === '/') {
      // On est déjà sur Home : scroll natif vers la section
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // On navigue vers Home avec query param, Home se charge du scroll à l'arrivée
      navigate(`/?scrollTo=${sectionId}`);
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { key: 'home', label: t('header.nav_links.home'), path: '/' },
    { key: 'portfolio', label: t('header.nav_links.portfolio'), path: '/portfolio' },
    { key: 'about', label: t('header.nav_links.about'), path: 'about' },
    { key: 'contact', label: t('header.nav_links.contact'), path: 'contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
      isScrolled ? 'bg-ink-950/95 backdrop-blur-md border-white/10' : 'bg-transparent border-transparent'
    }`}>
      <nav className="container mx-auto px-6 py-5">
        <div className="flex items-center justify-between">
          <Link to="/" className="font-display text-2xl md:text-3xl uppercase tracking-wide nav-logo">
            <span className="text-ember">AM</span>
            <span className="text-paper/40 mx-1">·</span>
            <span className="text-paper">Imagery</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10 font-mono">
            {navLinks.map((link) => (
              link.key === 'about' || link.key === 'contact' ? (
                <button
                  key={link.key}
                  onClick={() => handleScrollLink(link.path)}
                  className="text-base tracking-[0.12em] uppercase text-gray-400 hover:text-ember transition-colors duration-200 nav-link"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.key}
                  to={link.path}
                  className={`text-base tracking-[0.12em] uppercase transition-colors duration-200 nav-link ${
                    location.pathname === link.path
                      ? 'text-ember'
                      : 'text-gray-400 hover:text-ember'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <LanguageSwitcher className="nav-link" />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-paper hover:text-ember transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4 font-mono">
            {navLinks.map((link) => (
              link.key === 'about' || link.key === 'contact' ? (
                <button
                  key={link.key}
                  onClick={() => handleScrollLink(link.path)}
                  className="block text-base tracking-[0.12em] uppercase text-gray-400 hover:text-ember transition-colors duration-200"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.key}
                  to={link.path}
                  className={`block text-base tracking-[0.12em] uppercase transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-ember'
                      : 'text-gray-400 hover:text-ember'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )
            ))}
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
